import { useCallback, useEffect, useRef, useState } from 'react';

import { useUserState } from '@/state';

// Auto-wylogowanie po bezczynności. Znacznik trzymamy w localStorage (nie w pamięci),
// dzięki czemu aktywność w dowolnej zakładce liczy się do wszystkich, a wyłączenie
// komputera na noc zostaje wykryte przy następnym starcie aplikacji.
// Klucz zawiera 'auth', więc istniejące czyszczenie localStorage przy logout usuwa go.
const LAST_ACTIVITY_KEY = 'auth.lastActivity';

export const IDLE_LIMIT_MS = 60 * 60 * 1000; // wylogowanie po 60 min bezczynności
export const IDLE_WARNING_MS = 2 * 60 * 1000; // ostrzeżenie 2 min przed wylogowaniem

const ACTIVITY_THROTTLE_MS = 30 * 1000; // nie zapisujemy do localStorage częściej niż co 30 s
const CHECK_INTERVAL_MS = 1000; // co sekundę, żeby odliczanie w modalu było płynne

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

const readLastActivity = (): number => {
  try {
    const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
    const ts = raw ? Number(raw) : NaN;
    return Number.isFinite(ts) ? ts : 0;
  } catch {
    return 0;
  }
};

const writeLastActivity = (ts: number): void => {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(ts));
  } catch {
    /* ignore quota */
  }
};

interface IdleLogoutResult {
  isWarning: boolean;
  secondsLeft: number;
  stayLoggedIn: () => void;
}

export const useIdleLogout = (): IdleLogoutResult => {
  const { currentUser, logout } = useUserState();
  const [isWarning, setIsWarning] = useState(false);
  const [msLeft, setMsLeft] = useState(IDLE_WARNING_MS);

  const isWarningRef = useRef(false);
  const isLoggingOutRef = useRef(false);

  useEffect(() => {
    isWarningRef.current = isWarning;
  }, [isWarning]);

  const recordActivity = useCallback(() => {
    // Gdy ostrzeżenie jest już na ekranie, sam ruch myszy NIE przedłuża sesji —
    // wymagamy świadomego kliknięcia "Zostań zalogowany".
    if (isWarningRef.current) return;

    const now = Date.now();
    if (now - readLastActivity() < ACTIVITY_THROTTLE_MS) return;
    writeLastActivity(now);
  }, []);

  const stayLoggedIn = useCallback(() => {
    writeLastActivity(Date.now());
    setIsWarning(false);
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setIsWarning(false);
      return undefined;
    }

    isLoggingOutRef.current = false;

    // Świeże logowanie (logout wyczyścił klucz) — licznik startuje od teraz.
    if (!readLastActivity()) writeLastActivity(Date.now());

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, recordActivity, { passive: true });
    });

    const check = (): void => {
      const idleFor = Date.now() - readLastActivity();

      if (idleFor >= IDLE_LIMIT_MS) {
        if (isLoggingOutRef.current) return;
        isLoggingOutRef.current = true;
        setIsWarning(false);
        logout();
        return;
      }

      if (idleFor >= IDLE_LIMIT_MS - IDLE_WARNING_MS) {
        setIsWarning(true);
        setMsLeft(IDLE_LIMIT_MS - idleFor);
      } else {
        // Aktywność mogła przyjść z innej zakładki — wtedy chowamy ostrzeżenie.
        setIsWarning(false);
      }
    };

    check(); // od razu przy starcie — łapie sesję porzuconą np. na noc
    const intervalId = window.setInterval(check, CHECK_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, recordActivity);
      });
    };
  }, [currentUser, logout, recordActivity]);

  return {
    isWarning,
    secondsLeft: Math.max(0, Math.ceil(msLeft / 1000)),
    stayLoggedIn,
  };
};
